import { ColumnsType } from "antd/es/table";
import { formatLkr } from "../../utils/currency";
import dayjs from "dayjs";

function fmtLkr(v: any) {
  return formatLkr(Number(v || 0));
}

function fmtDate(v: any) {
  return v ? dayjs(v).format("MMM D, YYYY") : "N/A";
}

/** `fmtLkr` prints LKR 0.00 for null, which is right for a missing cell and
 *  wrong for a month the ledger never covered: that month has no figure at
 *  all, and drawing one is the exact lie the annual report is built to avoid. */
function fmtLkrOrNull(v: any) {
  return v === null || v === undefined ? "—" : formatLkr(Number(v));
}

/** Margin with no denominator is null, never 0 %. */
function fmtPct(v: any) {
  return v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`;
}

/** Dates on an ageing row are always present. The control row deliberately
 *  has none, and "N/A" three times before a sentence reads as three missing
 *  facts rather than one summary line. */
function fmtAgingDate(v: any) {
  return v ? dayjs(v).format("MMM D, YYYY") : "";
}

function fmtMonth(v: any) {
  return v ? dayjs(v).format("MMM YYYY") : "N/A";
}

// ---- 1. Daily Sales ----
export const dailySalesCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "sale_date", key: "sale_date", render: fmtDate },
  { title: "Restaurant", dataIndex: "restaurant_revenue", key: "restaurant_revenue", align: "right", render: fmtLkr },
  { title: "Room", dataIndex: "room_revenue", key: "room_revenue", align: "right", render: fmtLkr },
  { title: "Laundry", dataIndex: "laundry_revenue", key: "laundry_revenue", align: "right", render: fmtLkr },
  { title: "Services", dataIndex: "service_revenue", key: "service_revenue", align: "right", render: fmtLkr },
];

// ---- 2. Restaurant Sales ----
export const restaurantSalesCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "sale_date", key: "sale_date", render: fmtDate },
  { title: "Orders", dataIndex: "order_count", key: "order_count", align: "right" },
  { title: "Subtotal", dataIndex: "subtotal", key: "subtotal", align: "right", render: fmtLkr },
  { title: "Discounts", dataIndex: "discounts", key: "discounts", align: "right", render: fmtLkr },
  { title: "S.Charge", dataIndex: "service_charges", key: "service_charges", align: "right", render: fmtLkr },
  { title: "Tax", dataIndex: "taxes", key: "taxes", align: "right", render: fmtLkr },
  { title: "Total", dataIndex: "total_revenue", key: "total_revenue", align: "right", render: fmtLkr },
];

// ---- 3. Take Away Sales ----
export const takeAwaySalesCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "sale_date", key: "sale_date", render: fmtDate },
  { title: "Orders", dataIndex: "order_count", key: "order_count", align: "right" },
  { title: "Subtotal", dataIndex: "subtotal", key: "subtotal", align: "right", render: fmtLkr },
  { title: "Discounts", dataIndex: "discounts", key: "discounts", align: "right", render: fmtLkr },
  { title: "Tax", dataIndex: "taxes", key: "taxes", align: "right", render: fmtLkr },
  { title: "Total", dataIndex: "total_revenue", key: "total_revenue", align: "right", render: fmtLkr },
];

// ---- 4. Dine-In Sales ----
export const dineInSalesCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "sale_date", key: "sale_date", render: fmtDate },
  { title: "Orders", dataIndex: "order_count", key: "order_count", align: "right" },
  { title: "Tables Used", dataIndex: "tables_used", key: "tables_used", align: "right" },
  { title: "Total Revenue", dataIndex: "total_revenue", key: "total_revenue", align: "right", render: fmtLkr },
];

// ---- 5. Room Service Sales ----
export const roomServiceSalesCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "sale_date", key: "sale_date", render: fmtDate },
  { title: "Orders", dataIndex: "order_count", key: "order_count", align: "right" },
  { title: "Rooms Served", dataIndex: "rooms_served", key: "rooms_served", align: "right" },
  { title: "Total Revenue", dataIndex: "total_revenue", key: "total_revenue", align: "right", render: fmtLkr },
];

// ---- 6. Room Revenue ----
export const roomRevenueCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "sale_date", key: "sale_date", render: fmtDate },
  { title: "Rooms Occupied", dataIndex: "rooms_occupied", key: "rooms_occupied", align: "right" },
  { title: "Room Revenue", dataIndex: "room_revenue", key: "room_revenue", align: "right", render: fmtLkr },
];

// ---- 7. Meal Breakdown ----
export const mealBreakdownCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "sale_date", key: "sale_date", render: fmtDate },
  { title: "Breakfast", dataIndex: "breakfast", key: "breakfast", align: "right", render: fmtLkr },
  { title: "Lunch", dataIndex: "lunch", key: "lunch", align: "right", render: fmtLkr },
  { title: "Dinner", dataIndex: "dinner", key: "dinner", align: "right", render: fmtLkr },
  { title: "Total", dataIndex: "total", key: "total", align: "right", render: fmtLkr },
];

// ---- 8. Monthly Revenue ----
export const monthlyRevenueCols: ColumnsType<any> = [
  { title: "Month", dataIndex: "month", key: "month", render: fmtMonth },
  { title: "Bookings", dataIndex: "bookings", key: "bookings", align: "right" },
  { title: "Revenue", dataIndex: "revenue", key: "revenue", align: "right", render: fmtLkr },
];

// ---- 9. Bookings by Room Type ----
export const bookingsByRoomTypeCols: ColumnsType<any> = [
  { title: "Room Type", dataIndex: "room_type", key: "room_type" },
  { title: "Total Bookings", dataIndex: "total_bookings", key: "total_bookings", align: "right" },
  { title: "Total Revenue", dataIndex: "total_revenue", key: "total_revenue", align: "right", render: fmtLkr },
];

// ---- 10. Laundry ----
export const laundryCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "sale_date", key: "sale_date", render: fmtDate },
  { title: "Orders", dataIndex: "order_count", key: "order_count", align: "right" },
  { title: "Staff", dataIndex: "staff_name", key: "staff_name" },
  { title: "Revenue", dataIndex: "total_revenue", key: "total_revenue", align: "right", render: fmtLkr },
];

// ---- 11. Ironing ----
export const ironingCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "sale_date", key: "sale_date", render: fmtDate },
  { title: "Orders", dataIndex: "order_count", key: "order_count", align: "right" },
  { title: "Staff", dataIndex: "staff_name", key: "staff_name" },
  { title: "Revenue", dataIndex: "total_revenue", key: "total_revenue", align: "right", render: fmtLkr },
];

// ---- 12. Other Services ----
export const otherServicesCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "sale_date", key: "sale_date", render: fmtDate },
  { title: "Category", dataIndex: "category", key: "category" },
  { title: "Description", dataIndex: "description", key: "description" },
  { title: "Guest", dataIndex: "guest_name", key: "guest_name" },
  { title: "Qty", dataIndex: "quantity", key: "quantity", align: "right" },
  { title: "Unit Price", dataIndex: "unit_price", key: "unit_price", align: "right", render: fmtLkr },
  { title: "Total", dataIndex: "total", key: "total", align: "right", render: fmtLkr },
];

// ---- 13. Car Rental ----
export const carRentalCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "sale_date", key: "sale_date", render: fmtDate },
  { title: "Description", dataIndex: "description", key: "description" },
  { title: "Guest", dataIndex: "guest_name", key: "guest_name" },
  { title: "Room", dataIndex: "room_number", key: "room_number" },
  { title: "Qty", dataIndex: "quantity", key: "quantity", align: "right" },
  { title: "Unit Price", dataIndex: "unit_price", key: "unit_price", align: "right", render: fmtLkr },
  { title: "Total", dataIndex: "total", key: "total", align: "right", render: fmtLkr },
];

// ---- 14. Motorbike Rental ----
export const motorbikeRentalCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "sale_date", key: "sale_date", render: fmtDate },
  { title: "Description", dataIndex: "description", key: "description" },
  { title: "Guest", dataIndex: "guest_name", key: "guest_name" },
  { title: "Room", dataIndex: "room_number", key: "room_number" },
  { title: "Qty", dataIndex: "quantity", key: "quantity", align: "right" },
  { title: "Unit Price", dataIndex: "unit_price", key: "unit_price", align: "right", render: fmtLkr },
  { title: "Total", dataIndex: "total", key: "total", align: "right", render: fmtLkr },
];

// ---- 15. Car Wash ----
export const carWashCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "sale_date", key: "sale_date", render: fmtDate },
  { title: "Description", dataIndex: "description", key: "description" },
  { title: "Guest", dataIndex: "guest_name", key: "guest_name" },
  { title: "Room", dataIndex: "room_number", key: "room_number" },
  { title: "Qty", dataIndex: "quantity", key: "quantity", align: "right" },
  { title: "Unit Price", dataIndex: "unit_price", key: "unit_price", align: "right", render: fmtLkr },
  { title: "Total", dataIndex: "total", key: "total", align: "right", render: fmtLkr },
];

// ---- 16. Cashier ----
export const cashierCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "sale_date", key: "sale_date", render: fmtDate },
  { title: "Cashier", dataIndex: "cashier_name", key: "cashier_name" },
  { title: "Method", dataIndex: "payment_method", key: "payment_method" },
  { title: "Transactions", dataIndex: "transaction_count", key: "transaction_count", align: "right" },
  { title: "Total Collected", dataIndex: "total_collected", key: "total_collected", align: "right", render: fmtLkr },
];

// ---- 17. Payments ----
export const paymentsCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "sale_date", key: "sale_date", render: fmtDate },
  { title: "Method", dataIndex: "payment_method", key: "payment_method" },
  { title: "Count", dataIndex: "transaction_count", key: "transaction_count", align: "right" },
  { title: "Total", dataIndex: "total_amount", key: "total_amount", align: "right", render: fmtLkr },
  { title: "Completed", dataIndex: "completed", key: "completed", align: "right" },
  { title: "Pending", dataIndex: "pending_count", key: "pending_count", align: "right" },
  { title: "Failed", dataIndex: "failed", key: "failed", align: "right" },
];

// ---- 18. Outstanding Balances ----
export const outstandingBalancesCols: ColumnsType<any> = [
  { title: "Guest", dataIndex: "guest_name", key: "guest_name" },
  { title: "Room", dataIndex: "room_number", key: "room_number" },
  { title: "Type", dataIndex: "room_type", key: "room_type" },
  { title: "Check-In", dataIndex: "check_in_date", key: "check_in_date", render: fmtDate },
  { title: "Check-Out", dataIndex: "check_out_date", key: "check_out_date", render: fmtDate },
  { title: "Charges", dataIndex: "total_charges", key: "total_charges", align: "right", render: fmtLkr },
  { title: "Payments", dataIndex: "total_payments", key: "total_payments", align: "right", render: fmtLkr },
  { title: "Balance", dataIndex: "balance", key: "balance", align: "right", render: fmtLkr },
];

// ---- 19. Guest Folio ---- (columns for entries sub-table)
export const guestFolioCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "entry_date", key: "entry_date", render: fmtDate },
  { title: "Time", dataIndex: "entry_time", key: "entry_time", render: (v: string) => (v ? String(v).slice(0, 5) : "") },
  { title: "Category", dataIndex: "category", key: "category" },
  { title: "Description", dataIndex: "description", key: "description" },
  { title: "Qty", dataIndex: "quantity", key: "quantity", align: "right" },
  { title: "Unit Price", dataIndex: "unit_price", key: "unit_price", align: "right", render: fmtLkr },
  { title: "Discount", dataIndex: "discount", key: "discount", align: "right", render: fmtLkr },
  { title: "Tax", dataIndex: "tax", key: "tax", align: "right", render: fmtLkr },
  { title: "Total", dataIndex: "total", key: "total", align: "right", render: fmtLkr },
  { title: "Staff", dataIndex: "staff_name", key: "staff_name" },
];

// ---- 20. Weekly Statement ----
export const weeklyStatementCols: ColumnsType<any> = [
  { title: "Category", dataIndex: "category", key: "category" },
  { title: "Amount", dataIndex: "amount", key: "amount", align: "right", render: fmtLkr },
];

// ---- 21. Monthly Statement ----
export const monthlyStatementCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "sale_date", key: "sale_date", render: fmtDate },
  { title: "Room Revenue", dataIndex: "room_revenue", key: "room_revenue", align: "right", render: fmtLkr },
  { title: "Restaurant", dataIndex: "restaurant_revenue", key: "restaurant_revenue", align: "right", render: fmtLkr },
  { title: "Laundry", dataIndex: "laundry_revenue", key: "laundry_revenue", align: "right", render: fmtLkr },
  { title: "Services", dataIndex: "service_revenue", key: "service_revenue", align: "right", render: fmtLkr },
  { title: "Total Revenue", dataIndex: "total_revenue", key: "total_revenue", align: "right", render: fmtLkr },
  { title: "Payments", dataIndex: "total_payments", key: "total_payments", align: "right", render: fmtLkr },
  { title: "Net Balance", dataIndex: "net_balance", key: "net_balance", align: "right", render: fmtLkr },
];

// ---- 22. Tax / VAT ----
export const taxCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "sale_date", key: "sale_date", render: fmtDate },
  { title: "Source", dataIndex: "source", key: "source" },
  { title: "Description", dataIndex: "description", key: "description" },
  { title: "Tax Amount", dataIndex: "tax_amount", key: "tax_amount", align: "right", render: fmtLkr },
  { title: "Guest", dataIndex: "guest_name", key: "guest_name" },
];

// ---- 23. Discounts ----
export const discountsCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "sale_date", key: "sale_date", render: fmtDate },
  { title: "Source", dataIndex: "source", key: "source" },
  { title: "Description", dataIndex: "description", key: "description" },
  { title: "Discount", dataIndex: "discount_amount", key: "discount_amount", align: "right", render: fmtLkr },
  { title: "Guest", dataIndex: "guest_name", key: "guest_name" },
];

// ---- 24. Void / Cancelled ----
export const voidCancelledCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "sale_date", key: "sale_date", render: fmtDate },
  { title: "Type", dataIndex: "type", key: "type" },
  { title: "Reference", dataIndex: "reference", key: "reference" },
  { title: "Guest", dataIndex: "guest_name", key: "guest_name" },
  { title: "Amount", dataIndex: "amount", key: "amount", align: "right", render: fmtLkr },
];

// ---- 25. Item Sales ----
export const itemSalesCols: ColumnsType<any> = [
  { title: "Item", dataIndex: "item_name", key: "item_name" },
  { title: "Category", dataIndex: "category_name", key: "category_name" },
  { title: "Orders", dataIndex: "times_ordered", key: "times_ordered", align: "right" },
  { title: "Qty Sold", dataIndex: "total_quantity", key: "total_quantity", align: "right" },
  { title: "Revenue", dataIndex: "total_revenue", key: "total_revenue", align: "right", render: fmtLkr },
  { title: "Avg Price", dataIndex: "avg_price", key: "avg_price", align: "right", render: fmtLkr },
];

// ---- 26. Category Sales ----
export const categorySalesCols: ColumnsType<any> = [
  { title: "Category", dataIndex: "category_name", key: "category_name" },
  { title: "Orders", dataIndex: "order_count", key: "order_count", align: "right" },
  { title: "Items Sold", dataIndex: "items_sold", key: "items_sold", align: "right" },
  { title: "Revenue", dataIndex: "total_revenue", key: "total_revenue", align: "right", render: fmtLkr },
];

// ---- 27. Top Guests ----
export const topGuestsCols: ColumnsType<any> = [
  { title: "Guest", dataIndex: "full_name", key: "full_name" },
  { title: "Email", dataIndex: "email", key: "email" },
  { title: "Segment", dataIndex: "segment", key: "segment" },
  { title: "Stays", dataIndex: "total_stays", key: "total_stays", align: "right" },
  { title: "Total Spent", dataIndex: "total_spent", key: "total_spent", align: "right", render: fmtLkr },
];

// ---- 28. Finance (general ledger) ----
export const financeProfitLossCols: ColumnsType<any> = [
  { title: "Section", dataIndex: "section", key: "section" },
  { title: "Code", dataIndex: "code", key: "code", width: 80 },
  { title: "Account", dataIndex: "name", key: "name" },
  { title: "Amount", dataIndex: "amount", key: "amount", align: "right", render: fmtLkr },
];

export const financeTrialBalanceCols: ColumnsType<any> = [
  { title: "Code", dataIndex: "code", key: "code", width: 80 },
  { title: "Account", dataIndex: "name", key: "name" },
  { title: "Opening", dataIndex: "opening", key: "opening", align: "right", render: fmtLkr },
  { title: "Debit", dataIndex: "debit", key: "debit", align: "right", render: fmtLkr },
  { title: "Credit", dataIndex: "credit", key: "credit", align: "right", render: fmtLkr },
  { title: "Closing", dataIndex: "closing", key: "closing", align: "right", render: fmtLkr },
];

export const financeDayBookCols: ColumnsType<any> = [
  {
    title: "Posted",
    dataIndex: "posted_at",
    key: "posted_at",
    width: 140,
    render: (v: any) => (v ? dayjs(v).format("MMM D, HH:mm") : "—"),
  },
  { title: "Journal", dataIndex: "journal_type", key: "journal_type", width: 100 },
  { title: "Account", dataIndex: "account_code", key: "account_code", width: 80 },
  { title: "Account name", dataIndex: "account_name", key: "account_name" },
  { title: "Department", dataIndex: "department_name", key: "department_name", width: 130 },
  { title: "Description", dataIndex: "journal_description", key: "journal_description" },
  { title: "Debit", dataIndex: "debit", key: "debit", align: "right", render: fmtLkr },
  { title: "Credit", dataIndex: "credit", key: "credit", align: "right", render: fmtLkr },
];

export const financeCashBookCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "business_date", key: "business_date", render: fmtDate, width: 110 },
  { title: "Type", dataIndex: "account_type", key: "account_type", width: 75 },
  { title: "Code", dataIndex: "account_code", key: "account_code", width: 75 },
  { title: "Account", dataIndex: "account_name", key: "account_name" },
  { title: "Opening", dataIndex: "opening_balance", key: "opening_balance", align: "right", render: fmtLkr },
  { title: "Money in", dataIndex: "money_in", key: "money_in", align: "right", render: fmtLkr },
  { title: "Money out", dataIndex: "money_out", key: "money_out", align: "right", render: fmtLkr },
  { title: "Balance", dataIndex: "running_balance", key: "running_balance", align: "right", render: fmtLkr },
];

export const financeDailyCashCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "business_date", key: "business_date", render: fmtDate, width: 110 },
  { title: "Cash account", dataIndex: "account_name", key: "account_name" },
  { title: "Opening", dataIndex: "opening_balance", key: "opening_balance", align: "right", render: fmtLkr },
  { title: "Receipts", dataIndex: "receipts", key: "receipts", align: "right", render: fmtLkr },
  { title: "Payments", dataIndex: "payments", key: "payments", align: "right", render: fmtLkr },
  { title: "Bank deposits", dataIndex: "deposits_to_bank", key: "deposits_to_bank", align: "right", render: fmtLkr },
  { title: "Closing", dataIndex: "closing_balance", key: "closing_balance", align: "right", render: fmtLkr },
];

export const financeDailyCardCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "business_date", key: "business_date", render: fmtDate, width: 110 },
  { title: "Card account", dataIndex: "account_name", key: "account_name" },
  { title: "Opening", dataIndex: "opening_balance", key: "opening_balance", align: "right", render: fmtLkr },
  { title: "Collections", dataIndex: "collections", key: "collections", align: "right", render: fmtLkr },
  { title: "Settled gross", dataIndex: "settled_gross", key: "settled_gross", align: "right", render: fmtLkr },
  { title: "Fees", dataIndex: "fees", key: "fees", align: "right", render: fmtLkr },
  { title: "Net to bank", dataIndex: "settled_net", key: "settled_net", align: "right", render: fmtLkr },
  { title: "Closing", dataIndex: "closing_balance", key: "closing_balance", align: "right", render: fmtLkr },
];

export const financeDailyBankCols: ColumnsType<any> = [
  { title: "Date", dataIndex: "business_date", key: "business_date", render: fmtDate, width: 110 },
  { title: "Bank account", dataIndex: "account_name", key: "account_name" },
  { title: "Opening", dataIndex: "opening_balance", key: "opening_balance", align: "right", render: fmtLkr },
  { title: "Cash deposits", dataIndex: "cash_deposits", key: "cash_deposits", align: "right", render: fmtLkr },
  { title: "Card settlements", dataIndex: "card_settlements", key: "card_settlements", align: "right", render: fmtLkr },
  { title: "Other in", dataIndex: "other_in", key: "other_in", align: "right", render: fmtLkr },
  { title: "Payments", dataIndex: "money_out", key: "money_out", align: "right", render: fmtLkr },
  { title: "Closing", dataIndex: "closing_balance", key: "closing_balance", align: "right", render: fmtLkr },
];

export const financeReconciliationCols: ColumnsType<any> = [
  { title: "Section", dataIndex: "section", key: "section", width: 170 },
  { title: "Item", dataIndex: "label", key: "label" },
  { title: "Count", dataIndex: "count", key: "count", align: "right" },
  { title: "Amount", dataIndex: "amount", key: "amount", align: "right", render: fmtLkr },
];

// Occupancy / ADR / RevPAR sit in the same two columns as the room rows so
// this table, its CSV and its PDF all line up. A rate with a zero
// denominator arrives as "n/a" from the flattener — never as 0.
export const financeRoomPerformanceCols: ColumnsType<any> = [
  { title: "Room / KPI", dataIndex: "room_number", key: "room_number" },
  { title: "Type / value", dataIndex: "room_type", key: "room_type" },
  { title: "Nights", dataIndex: "nights_sold", key: "nights_sold", align: "right" },
  {
    title: "Revenue",
    dataIndex: "room_revenue",
    key: "room_revenue",
    align: "right",
    // KPI rows carry no revenue; a dash beats a fabricated LKR 0.
    render: (v: any) => (v === null || v === undefined ? "—" : fmtLkr(v)),
  },
];

// KPI rows carry no order count / revenue; a dash beats a fabricated LKR 0.
export const financeRestaurantPerformanceCols: ColumnsType<any> = [
  { title: "Line / channel", dataIndex: "label", key: "label" },
  { title: "Detail", dataIndex: "detail", key: "detail" },
  { title: "Orders", dataIndex: "orders", key: "orders", align: "right" },
  {
    title: "Revenue",
    dataIndex: "revenue",
    key: "revenue",
    align: "right",
    render: (v: any) => (v === null || v === undefined ? "—" : fmtLkr(v)),
  },
];

// The shelf, in the order a storekeeper reads it. `value` is the movement sum
// rather than qty × cost — the figure the control against GL 1200 proves.
export const financeStockValuationCols: ColumnsType<any> = [
  { title: "Code", dataIndex: "code", key: "code", width: 100 },
  { title: "Item", dataIndex: "name", key: "name" },
  { title: "Category", dataIndex: "category", key: "category", width: 110 },
  { title: "Unit", dataIndex: "unit", key: "unit", width: 70 },
  { title: "On hand", dataIndex: "on_hand", key: "on_hand", align: "right" },
  { title: "Unit cost", dataIndex: "avg_cost", key: "avg_cost", align: "right", render: fmtLkr },
  { title: "Value", dataIndex: "value", key: "value", align: "right", render: fmtLkr },
];

// A KPI list, not a ledger: every row carries a label and a detail, and only
// some carry money — a dash beats a fabricated LKR 0.
export const financeFoodCostCols: ColumnsType<any> = [
  { title: "Line", dataIndex: "label", key: "label", width: 320 },
  { title: "Detail", dataIndex: "detail", key: "detail" },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    align: "right",
    render: (v: any) => (v === null || v === undefined ? "—" : fmtLkr(v)),
  },
];

export const financePurchaseRegisterCols: ColumnsType<any> = [
  { title: "Invoice", dataIndex: "voucher_no", key: "voucher_no", width: 130 },
  { title: "Date", dataIndex: "business_date", key: "business_date", width: 120, render: fmtDate },
  { title: "Supplier", dataIndex: "party", key: "party" },
  { title: "Department", dataIndex: "department_name", key: "department_name", width: 130 },
  { title: "Status", dataIndex: "status", key: "status", width: 90 },
  { title: "Net", dataIndex: "amount", key: "amount", align: "right", render: fmtLkr },
  { title: "Tax", dataIndex: "tax", key: "tax", align: "right", render: fmtLkr },
  { title: "Total", dataIndex: "total", key: "total", align: "right", render: fmtLkr },
];

// One line per employee per run: what they earned, what was withheld, and
// what it cost the resort above their pay.
export const financePayrollRegisterCols: ColumnsType<any> = [
  { title: "Period", dataIndex: "period_month", key: "period_month", width: 100, render: fmtDate },
  { title: "Employee", dataIndex: "employee_name", key: "employee_name", width: 190 },
  { title: "Department", dataIndex: "department_name", key: "department_name", width: 140 },
  { title: "Basic", dataIndex: "basic_salary", key: "basic_salary", align: "right", render: fmtLkr },
  { title: "Allowances", dataIndex: "allowances", key: "allowances", align: "right", render: fmtLkr },
  { title: "Gross", dataIndex: "gross_amount", key: "gross_amount", align: "right", render: fmtLkr },
  { title: "EPF/ETF", dataIndex: "epf_etf", key: "epf_etf", align: "right", render: fmtLkr },
  { title: "APIT", dataIndex: "apit", key: "apit", align: "right", render: fmtLkr },
  { title: "Net pay", dataIndex: "net_pay", key: "net_pay", align: "right", render: fmtLkr },
  {
    title: "Employer cost",
    dataIndex: "employer_cost",
    key: "employer_cost",
    align: "right",
    render: fmtLkr,
  },
];

// One debt per row, in the order an accountant works a list: who owes, when
// it fell due, how late it is, how much. The control against GL 2010 / 1100
// is a verdict on the whole list, so it gets a row of its own with no amount
// — putting a variance in the money column would let the totals add it up.
export const financeApAgingCols: ColumnsType<any> = [
  { title: "Voucher", dataIndex: "reference", key: "reference", width: 130 },
  { title: "Supplier", dataIndex: "party", key: "party" },
  { title: "Kind", dataIndex: "kind", key: "kind", width: 90 },
  { title: "Invoiced", dataIndex: "business_date", key: "business_date", width: 120, render: fmtAgingDate },
  { title: "Due", dataIndex: "due_date", key: "due_date", width: 120, render: fmtAgingDate },
  { title: "Days overdue", dataIndex: "days_overdue", key: "days_overdue", align: "right" },
  { title: "Bucket", dataIndex: "bucket", key: "bucket", width: 90 },
  { title: "Amount", dataIndex: "amount", key: "amount", align: "right", render: fmtLkr },
];

export const financeArAgingCols: ColumnsType<any> = [
  { title: "Ref", dataIndex: "reference", key: "reference", width: 150 },
  { title: "Debtor", dataIndex: "party", key: "party" },
  { title: "Type", dataIndex: "debtor_type", key: "debtor_type", width: 90 },
  { title: "From", dataIndex: "business_date", key: "business_date", width: 120, render: fmtAgingDate },
  { title: "Due", dataIndex: "due_date", key: "due_date", width: 120, render: fmtAgingDate },
  { title: "Days overdue", dataIndex: "days_overdue", key: "days_overdue", align: "right" },
  { title: "Bucket", dataIndex: "bucket", key: "bucket", width: 90 },
  { title: "Amount", dataIndex: "amount", key: "amount", align: "right", render: fmtLkr },
];

// Twelve rows, one per month, in the order the year runs. `note` carries the
// distinction a spreadsheet cannot: a month the ledger never covered has no
// figure, a month it covered but never posted has a real zero, and only the
// latter may be drawn as a bar.
export const financeAnnualPnlCols: ColumnsType<any> = [
  { title: "Month", dataIndex: "label", key: "label", width: 80 },
  { title: "Note", dataIndex: "note", key: "note" },
  { title: "Income", dataIndex: "income", key: "income", align: "right", render: fmtLkrOrNull },
  { title: "Expense", dataIndex: "expense", key: "expense", align: "right", render: fmtLkrOrNull },
  { title: "Profit", dataIndex: "profit", key: "profit", align: "right", render: fmtLkrOrNull },
  { title: "Margin", dataIndex: "margin", key: "margin", width: 90, align: "right", render: fmtPct },
];

// ---- Lookup map ----
export const REPORT_COLUMNS: Record<string, ColumnsType<any>> = {
  "daily-sales": dailySalesCols,
  "restaurant-sales": restaurantSalesCols,
  "take-away-sales": takeAwaySalesCols,
  "dine-in-sales": dineInSalesCols,
  "room-service-sales": roomServiceSalesCols,
  "room-revenue": roomRevenueCols,
  "meal-breakdown": mealBreakdownCols,
  "monthly-revenue": monthlyRevenueCols,
  "bookings-by-room-type": bookingsByRoomTypeCols,
  "laundry": laundryCols,
  "ironing": ironingCols,
  "other-services": otherServicesCols,
  "car-rental": carRentalCols,
  "motorbike-rental": motorbikeRentalCols,
  "car-wash": carWashCols,
  "cashier": cashierCols,
  "payments": paymentsCols,
  "outstanding-balances": outstandingBalancesCols,
  "guest-folio": guestFolioCols,
  "weekly-statement": weeklyStatementCols,
  "monthly-statement": monthlyStatementCols,
  "tax": taxCols,
  "discounts": discountsCols,
  "void-cancelled": voidCancelledCols,
  "item-sales": itemSalesCols,
  "category-sales": categorySalesCols,
  "top-guests": topGuestsCols,
  "finance-profit-loss": financeProfitLossCols,
  "finance-trial-balance": financeTrialBalanceCols,
  "finance-day-book": financeDayBookCols,
  "finance-cash-book": financeCashBookCols,
  "finance-daily-cash": financeDailyCashCols,
  "finance-daily-card": financeDailyCardCols,
  "finance-daily-bank": financeDailyBankCols,
  "finance-reconciliation": financeReconciliationCols,
  "finance-room-performance": financeRoomPerformanceCols,
  "finance-restaurant-performance": financeRestaurantPerformanceCols,
  "finance-stock-valuation": financeStockValuationCols,
  "finance-food-cost": financeFoodCostCols,
  "finance-purchase-register": financePurchaseRegisterCols,
  "finance-payroll-register": financePayrollRegisterCols,
  "finance-ap-aging": financeApAgingCols,
  "finance-ar-aging": financeArAgingCols,
  "finance-annual-pnl": financeAnnualPnlCols,
};
